import React, { useState, useEffect } from 'react';
import '../../VendorStyles/Neworders.css';
import axiosVendorInstance from '../../axiosVendorInstance';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import { OrderContextuseOrders } from '../../ordersCount';
import Loader from '../../Components/Loader/Loader';
import { useNotifications } from '../User/Notification/NotificationContext';
import NewOrderPopup from './NewOrderPopup';
import ApproxTimePopup from './ApproxTimePopup';
import axiosUserInstance from '../../axiosUserInstance';
import axios from 'axios';
import axiosPaymentInstance from '../../axiosPaymentinstance';
// Note: directory name is case-sensitive in Linux builds
import createLogger from '../../Utils/debugLogger';

// Build a plain-text receipt suitable for RawBT printing
const buildReceiptText = (order = {}, items = [], meta = {}) => {
    try {
        const pad = (s = '', n = 16) => String(s).slice(0, n).padEnd(n, ' ');
        const lines = [];
        lines.push('      OH MY FOOD');
        lines.push('   --------------------');
        lines.push(`   Order ID: #${order?.ID ?? order?.orderId ?? ''}`);
        if (order?.user?.first_name || order?.user?.last_name) {
            const nm = `${order?.user?.first_name || ''} ${order?.user?.last_name || ''}`.trim();
            lines.push(`   Customer: ${nm}`);
        }
        lines.push('   Items:');
        (items || []).forEach((it) => {
            const name = it?.Menu_Item_Name || it?.name || 'Item';
            const qty = parseInt(it?.Quantity ?? it?.qty ?? 1, 10) || 1;
            const price = parseFloat(it?.Menu_Item_Price ?? it?.price ?? 0) || 0;
            const total = (price * qty).toFixed(2);
            lines.push(`   ${pad(name, 15)} x${String(qty).padEnd(2, ' ')} $${total}`);
        });
        lines.push('   --------------------');
        const grand = parseFloat(order?.finalAmount ?? meta?.total ?? 0) || 0;
        lines.push(`   Total:${' '.repeat(11)}$${grand.toFixed(2)}`);
        const stamp = meta?.timestamp || new Date().toLocaleString();
        lines.push(`   Time: ${stamp}`);
        lines.push('   --------------------');
        lines.push('   Thank you for choosing');
        lines.push('        Oh My Food!');
        return lines.join('\n');
    } catch (_) {
        return 'OH MY FOOD\nOrder Accepted';
    }
};

const openRawBTIntent = async (text) => {
    const payload = encodeURIComponent(text);
    const tryOpen = (url) => {
        try { window.location.href = url; } catch (_) { /* no-op */ }
    };
    // 1) Direct RawBT print action
    tryOpen(`intent://#Intent;package=ru.a402d.rawbtprinter;action=ru.a402d.rawbtprinter.action.PRINT;S.text=${payload};end`);
    // 2) Fallback: Android SEND intent to RawBT
    setTimeout(() => {
        tryOpen(`intent://#Intent;action=android.intent.action.SEND;type=text/plain;S.android.intent.extra.TEXT=${payload};package=ru.a402d.rawbtprinter;end`);
    }, 600);
    // 3) Fallback: Web Share API
    setTimeout(async () => {
        if (navigator.share) {
            try { await navigator.share({ text }); return; } catch (_) { /* user canceled */ }
        }
    }, 1200);
};

function NewOrders() {
    const logger = createLogger('Vendor/NewOrders');
    const { addNotification } = useNotifications();
    const [deliveryOffsetMinutes, setDeliveryOffsetMinutes] = useState(20);

    const { newOrdersCount, setNewOrdersCount } = OrderContextuseOrders();
    const [showPopup, setShowPopup] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showSecondPopup, setShowSecondPopup] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [orders, setOrders] = useState([]);
    const [vendorResponse, setVendorResponse] = useState([]);
    const [buyerdetails, setBuyerDetails] = useState([]);
    const pageSize = 10;
    const [vendorID, setVendorID] = useState();
    const REACT_APP_SHIPDAY_API_URL = process.env.REACT_APP_SHIPDAY_API_URL; 
    const PRINTING_API = process.env.REACT_APP_PRINTING_BACKEND_URL;

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const fetchVendorID = async () => {
        try {
            setLoading(true);
            const vendorResponse = await axiosVendorInstance.get('/vendors/account');
            const fetchedVendorID = vendorResponse?.data?.getVendorInfo[0]?.ID;

            setVendorResponse(vendorResponse?.data?.getVendorInfo[0]);
            if (fetchedVendorID) {
                setVendorID(fetchedVendorID);
            }
        } catch (error) {
            console.error('Error fetching vendor ID:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchNewOrders = async () => {
        setLoading(true);

        try {
            const response = await axiosVendorInstance.get(
                `/Order/vendor/${vendorID}?pageSize=${pageSize}&pageNumber=${page}&statusId=1`,
                { skipAuth: true }
            );

            const incomingData = response?.data?.allOrdersDetails;

            if (incomingData?.length === 0 && page > 1) {
                setPage(page - 1);
            } else {
                setOrders(incomingData);
                setTotalPages(response?.data?.totalPages || 1);
            }

            setLoading(false);
        } catch (error) {
            console.error(error.message);
            setLoading(false);
        }
    };

    useEffect(() => {
        let interval;

        if (vendorID) {
            fetchNewOrders();

            interval = setInterval(() => {
                fetchNewOrders();
            }, 10000);
        }

        return () => clearInterval(interval);
    }, [vendorID]);

    useEffect(() => {
        fetchVendorID();
    }, []);

    useEffect(() => {
        if (vendorID) {
            fetchNewOrders();
        }
    }, [page, vendorID]);

    const handleOrderClick = (order) => {
        const createdAt = new Date(order.Created_at);
        const formattedTime = createdAt.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
        const formattedDate = `${createdAt.getDate()} ${createdAt.toLocaleString('en-US', {
            month: 'short'
        })}`;
        setSelectedOrder({
            order,
            time: formattedTime,
            date: formattedDate,
        });
        setShowPopup(true);
        setLoading(true);
        axiosVendorInstance
            .get(`/Order/user/orderinvoice/${order.ID}`)
            .then((response) => {
                const selectedOrderDetails =
                    response?.data?.invoice[0]['0']?.Invoice_Details?.Order_Details;
                setBuyerDetails(response?.data?.invoice[0]['0']?.Invoice_Details);
                setItems(selectedOrderDetails);
                setLoading(false);
            })
            .catch((error) => console.error('Error fetching customer data:', error));
    };

    // Formatters in UTC for Shipday
    const formatUTCTime = (date) => {
        const hh = String(date.getUTCHours()).padStart(2, '0');
        const mm = String(date.getUTCMinutes()).padStart(2, '0');
        const ss = String(date.getUTCSeconds()).padStart(2, '0');
        return `${hh}:${mm}:${ss}`; // 24h UTC
    };
    const formatUTCDate = (date) => {
        const y = date.getUTCFullYear();
        const m = String(date.getUTCMonth() + 1).padStart(2, '0');
        const d = String(date.getUTCDate()).padStart(2, '0');
        return `${y}-${m}-${d}`; // YYYY-MM-DD in UTC
    };
    console.log('buyerdetails',buyerdetails);
    const handleAcceptOrder = async () => {
        if (!selectedOrder) return;

        try {
        // 1. Confirm payment
            logger.info('Accepting order: confirm payment', { vendorID, orderId: selectedOrder?.order?.ID });
            const paymentResponse = await axiosPaymentInstance.post('/payment/confirm', {
                orderId: selectedOrder?.order?.ID
            });

            if (!paymentResponse.data.success) {
                console.error('❌ Payment confirmation failed:', paymentResponse.data.error);
                return;
            }

            logger.info('Payment confirmed', { orderId: selectedOrder?.order?.ID });

            // 2. Update order status
            logger.info('Updating order status to accepted', { vendorID, orderId: selectedOrder?.order?.ID });
            await axiosVendorInstance.put(
                `/Order/Vendor/${vendorID}/order/${selectedOrder?.order?.ID}/status`,
                { status_id: 2 },
                { skipAuth: true }
            );
            logger.info('Order status updated', { orderId: selectedOrder?.order?.ID });

            // 2b. Trigger printer notification (non-blocking)
            try {
                // Determine backend URL from settings override or env
                let printerCfg = {};
                try {
                    const rawCfg = localStorage.getItem(`printerSettings:${vendorID}`);
                    if (rawCfg) printerCfg = JSON.parse(rawCfg);
                } catch (e) {
                    logger.warn('Could not parse printer settings from storage', { message: e?.message });
                }
                const backendUrl = (printerCfg.backendUrl || PRINTING_API || `http://${window.location.hostname}:8080`).replace(/\/$/, '');
                const useBackend = !!(printerCfg.backendUrl || PRINTING_API);
                if (useBackend) {
                    const order = selectedOrder.order || {};
                    const items = (selectedOrder.items || []).map((it) => ({
                        name: it?.Menu_Item_Name || 'Item',
                        qty: parseInt(it?.Quantity, 10) || 1,
                        price: parseFloat(it?.Menu_Item_Price) || 0,
                    }));
                    if (printerCfg && printerCfg.enableAutoPrint === false) {
                        logger.info('Auto print disabled in settings; skipping notify');
                        return;
                    }
                    const payload = {
                        restaurantId: vendorID,
                        items,
                        total: parseFloat(order.finalAmount) || 0,
                        timestamp: new Date().toISOString(),
                        printer: printerCfg,
                    };
                    logger.info('Notifying printer backend', { endpoint: `${backendUrl}/api/orders/${order.ID}/accept`, payload });
                    axios.post(`${backendUrl}/api/orders/${order.ID}/accept`, payload).catch((e) => {
                        logger.warn('Printer notify failed (ignored)', { message: e?.message });
                        // Fallback: Try local Android RawBT intent
                        try {
                            if (/Android/i.test(navigator.userAgent)) {
                                const text = buildReceiptText(order, selectedOrder.items || [], { total: payload.total, timestamp: payload.timestamp });
                                openRawBTIntent(text);
                            }
                        } catch (_) { /* no-op */ }
                    });
                } else {
                    // No backend configured: try direct RawBT intent on Android
                    try {
                        if (printerCfg && printerCfg.enableAutoPrint === false) {
                            logger.info('Auto print disabled in settings; skipping');
                        } else if (/Android/i.test(navigator.userAgent)) {
                            const order = selectedOrder.order || {};
                            const text = buildReceiptText(order, selectedOrder.items || [], { total: parseFloat(order.finalAmount) || 0, timestamp: new Date().toISOString() });
                            openRawBTIntent(text);
                        } else {
                            logger.info('No backend and not Android; skipping direct print');
                        }
                    } catch (e) {
                        logger.warn('Direct RawBT print failed (ignored)', { message: e?.message });
                    }
                }
            } catch (e) {
                logger.warn('Printer notify error (ignored)', { message: e?.message });
            }

            // 3. UI state update
            setOrders((prevOrders) =>
                prevOrders.filter((order) => order.ID !== selectedOrder?.order?.ID)
            );
            setShowPopup(false);
            setShowSecondPopup(true);
            setNewOrdersCount((prev) => prev - 1);
            fetchNewOrders();

            // 4. Send notification
            const notificationPayload = {
                from_user_id: selectedOrder?.order?.user_id,
                to_user_id: vendorID,
                type: 'order_accepted',
                action_id: selectedOrder?.order?.ID,
                notification_boundary: 'private',
                is_read: false,
            };
            addNotification(notificationPayload);


            // 5. Prepare delivery/pickup times in UTC
            const now = new Date();
            const addMinutes = (base, mins) => new Date(base.getTime() + mins * 60000);
            const pickupTime = addMinutes(now, 10);
            const deliveryTime = addMinutes(now, deliveryOffsetMinutes);


            // 6. Prepare Shipday delivery object
            const order = selectedOrder.order;
            const items = selectedOrder.items || [];
 
            const pickupLat = parseFloat(buyerdetails?.Restaurant_Details?.Restaurant_Location?.split(',')[0]);
            const pickupLng = parseFloat(buyerdetails?.Restaurant_Details?.Restaurant_Location?.split(',')[1]);

            const customerAddress = buyerdetails?.Buyer_Details?.Selected_Address;
            const shipdayOrderData = {
                orderId: `${order.ID}`,
                customerId: `${order.user?.ID}`,
                customerName: `${order.user?.first_name} ${order.user?.last_name}`.trim(),
                customerAddress: customerAddress,
                customerEmail: `${buyerdetails?.Buyer_Details?.Email}`,
                customerPhone: `${buyerdetails?.Buyer_Details?.Mobile}`,
                restaurantName: vendorResponse?.Name,
                restaurantAddress: [
                    buyerdetails?.Restaurant_Details?.Restaurant_Address,
                    buyerdetails?.Restaurant_Details?.Restaurant_City,
                    buyerdetails?.Restaurant_Details?.Restaurant_State,
                    buyerdetails?.Restaurant_Details?.Restaurant_Country,
                    buyerdetails?.Restaurant_Details?.Restaurant_Pincode
                ].filter(Boolean).join(', '),
                restaurantPhone: vendorResponse?.Phone_number,
                totalCost: order.finalAmount,
                deliveryFees: order.deliveryFee || 0,
                deliveryTips: order.delivery_tip || 0,
                discountAmount: order.discount || 0,
                pickupLat,
                pickupLng,
                // deliveryLat,
                // deliveryLng,
                // Send UTC date/time to Shipday
                deliveryDate: formatUTCDate(deliveryTime),
                deliveryTime: formatUTCTime(deliveryTime),
                pickupTime: formatUTCTime(pickupTime),
                paymentMethod: 'credit_card', // or 'CARD', mapped correctly in backend
                items
            };

            console.log('📦 Shipday Order Data:', shipdayOrderData);

            // 7. Create delivery order in Shipday
            const shipdayResponse = await axiosUserInstance.post('/shipday/create', shipdayOrderData);
            const shipdayOrderId = shipdayResponse?.data?.data?.orderId;

            console.log('🚀 Shipday order created:', shipdayResponse?.data);

            if (!shipdayOrderId) {
                console.warn('🚫 Missing Shipday order ID');
                return;
            }

            // 8. Assign driver (single call, no fallback)
            await assignDriver(shipdayOrderId);

        } catch (error) {
            console.error('❌ Order accept error:', error?.response?.data || error.message);
        } finally {
            fetchNewOrders();
            setShowPopup(false);
        }
    };

    const assignDriver = async (shipdayOrderId) => {
        try {
            console.log('🚚 Assigning driver in Shipday...');

            const res = await axios.post(
                'https://api.shipday.com/on-demand/assign',
                {
                    name: 'shipday',
                    orderId: shipdayOrderId
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Basic ${REACT_APP_SHIPDAY_API_URL}`
                    }
                }
            );

            console.log('✅ Driver assigned:', res.data);
        } catch (error) {
            console.error('❌ Driver assignment failed:', error.response?.data || error.message);
        }
    };

    const handleDeclineOrder = () => {
        if (selectedOrder) {
            axiosVendorInstance
                .put(`/Order/Vendor/${vendorID}/order/${selectedOrder?.order?.ID}/status`, {
                    status_id: 3,
                })
                .then(async () => {
                    setOrders((prevOrders) =>

                        prevOrders.filter((order) => order.ID !== selectedOrder?.order?.ID)
                    );
                    setShowPopup(false);
                    setShowSecondPopup(false);
                    setNewOrdersCount(newOrdersCount - 1);
                    fetchNewOrders();

                    const response = await axiosPaymentInstance.post('/payment/confirm',{
                        orderId: selectedOrder?.order?.ID,
                        forceCancel: true

                    });

                    console.log(response, 'response');
                    const notificationPayload = {
                        from_user_id: selectedOrder?.order?.user_id,
                        to_user_id: vendorID,
                        type: 'order_canceled',
                        action_id: selectedOrder?.order?.ID,
                        notification_boundary: 'private',
                        is_read: false,
                    };
                    addNotification(notificationPayload);
                })
                .catch((error) => console.error('Error updating order status:', error));
        }
        // fetchNewOrders();
    };

    const handlePrintNow = () => {
        try {
            if (!selectedOrder) return;
            if (/Android/i.test(navigator.userAgent)) {
                const order = selectedOrder.order || {};
                const text = buildReceiptText(order, selectedOrder.items || [], { total: parseFloat(order?.finalAmount) || 0, timestamp: new Date().toISOString() });
                openRawBTIntent(text);
            } else {
                alert('RawBT quick print works on Android devices.');
            }
        } catch (e) {
            console.warn('Print-now failed', e);
        }
    };

    return (
        <div className="new-orders-main">
            {loading ? (
                <Loader />
            ) : (
                <div>
                    {orders && orders?.length > 0 ? (
                        orders?.map((order) => {
                            const createdAt = new Date(order.Created_at);
                            const time = createdAt.toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                            });
                            const date = `${createdAt.getDate()} ${createdAt.toLocaleString(
                                'en-US',
                                { month: 'short' }
                            )}`;

                            return (
                                <div
                                    key={order.ID}
                                    className="single-order"
                                    onClick={() => handleOrderClick(order)}
                                >
                                    <div className="orderid">
                                        <p className="order-details1">
                                            Order ID:<span className="order-id">{order.ID}</span>
                                        </p>
                                    </div>
                                    <div className="date-and-time">
                                        <p className="order-details2">{time}</p>
                                        <p className="order-details3">{date}</p>
                                    </div>
                                    <div className="view-new-order-button-vendor">
                                        view
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="no-data-message">No data</div>
                    )}

                    {orders?.length > 0 && totalPages > 1 && (
                        <div className="pagination-container">
                            <Stack spacing={2}>
                                <Pagination
                                    sx={{
                                        '& .MuiPagination-ul': {
                                            color: '#333',
                                            padding: 1,
                                            borderRadius: 2,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            listStyleType: 'none',
                                        },
                                    }}
                                    count={totalPages}
                                    page={page}
                                    onChange={handlePageChange}
                                    showFirstButton
                                    showLastButton
                                />
                            </Stack>
                        </div>
                    )}

                    {showPopup && selectedOrder && (
                        <NewOrderPopup
                            showPopup={showPopup}
                            setShowPopup={setShowPopup}
                            selectedOrder={selectedOrder}
                            handleAcceptOrder={handleAcceptOrder}
                            handleDeclineOrder={handleDeclineOrder}
                            handlePrintNow={handlePrintNow}
                            items={items}
                            loading={loading}
                        />
                    )}

                    {showSecondPopup && (
                        <ApproxTimePopup
                            setShowSecondPopup={setShowSecondPopup}
                            deliveryOffsetMinutes={deliveryOffsetMinutes}
                            setDeliveryOffsetMinutes={setDeliveryOffsetMinutes}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

export default NewOrders;
